/**
 * Training Program API Client
 * Canonical interface for training plan generation and retrieval.
 */

import { edgeInvoke } from '@/lib/edgeInvoke';
import { supabase } from '@/integrations/supabase/client';

export type TrainingPlanIntake = {
  practice_frequency: '1-2_per_week' | '3-4_per_week' | '5+_per_week';
  session_length: '15-20_minutes' | '30_minutes' | '45+_minutes';
  primary_goal?: 'improve_ball_striking' | 'be_more_consistent' | 'score_better' | 'fix_specific_miss';
};

export interface GenerateTrainingProgramRequest {
  /**
   * PLA-138: Snapshot V2 run ID is REQUIRED for training plan generation.
   * Legacy snapshot_id and analysis_run_id are no longer supported.
   */
  snapshot_v2_run_id: string;
  intake: TrainingPlanIntake;
  action: 'new' | 'replace';
  skill_level?: 'beginner' | 'intermediate' | 'advanced' | 'low_handicap' | 'scratch_plus';
}

export interface GenerateTrainingProgramResponse {
  planId: string;
  primary_track: string;
  weeks: any[];
  daily_blocks: any[];
  range_sessions: any[];
  at_home_sessions: any[];
  pressure_ladder: any[];
  on_course_transfer: any[];
}

export interface TrainingPlanResponse {
  plan: {
    id: string;
    user_id: string;
    skill_level: string;
    swing_quality?: number | null;
    peer_label?: string | null;
    status?: string | null;
    plan_json: any;
    created_at: string;
    updated_at?: string | null;
  };
}

/**
 * Generate a training program from snapshot or analysis intake.
 */
export async function generateTrainingProgram(
  request: GenerateTrainingProgramRequest
): Promise<GenerateTrainingProgramResponse> {
  return edgeInvoke<GenerateTrainingProgramResponse>('training-program-generate', request);
}

/**
 * Get latest training plan for the current user.
 * Uses direct table access to avoid legacy edge functions.
 */
export async function getLatestTrainingPlan(): Promise<TrainingPlanResponse | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('Not authenticated');
  }

  const { data: plan, error } = await supabase
    .from('user_training_plans')
    .select('id, user_id, plan_json, skill_level, swing_quality, peer_label, status, created_at, updated_at')
    .eq('user_id', session.user.id)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !plan) {
    return null;
  }

  return { plan };
}
